import React from 'react'
import { Card, CardContent, Grid, Tooltip, Typography } from '@mui/material'

import { IDwUsageStatus, formatDwDay } from '../../Services/datawarehouse'

interface Props {
	status: IDwUsageStatus
}

// ⚠️ Les tuiles s'étirent à la hauteur de la plus haute, sinon la plus bavarde
// (la couverture, qui porte deux dates) désaligne toute la rangée. `stretch` sur
// le conteneur ne suffit pas : c'est la Card qui doit prendre 100 % de la
// hauteur que la grille lui donne.
const Tile: React.FC<{ label: string; value: string; hint?: string; warn?: boolean }> = ({
	label,
	value,
	hint,
	warn,
}) => (
	<Grid item xs={6} md={3} sx={{ display: 'flex' }}>
		<Tooltip title={hint || ''}>
			<Card sx={{ width: '100%', display: 'flex' }}>
				<CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
					<Typography variant="caption" color="text.secondary" noWrap>
						{label}
					</Typography>
					<Typography variant="h5" color={warn ? 'warning.main' : undefined}>
						{value}
					</Typography>
				</CardContent>
			</Card>
		</Tooltip>
	</Grid>
)

// #region DwStatusTiles
export const DwStatusTiles: React.FC<Props> = ({ status }) => {
	const lastMeasured = status.lastMeasuredAt
		? new Date(status.lastMeasuredAt).toLocaleString('fr-FR', {
				dateStyle: 'short',
				timeStyle: 'short',
		  })
		: 'jamais'

	return (
		<Grid container spacing={2} alignItems="stretch">
			<Tile
				label="Dernière lecture de l’export"
				value={lastMeasured}
				hint="Quand le cron a lu l’export Cloud Billing pour la dernière fois. Distinct de la dernière journée couverte : Google publie avec un jour de retard."
			/>
			<Tile
				label="Couverture"
				// Format court : « 22/08/2026 → 27/09/2026 » tient sur une ligne là où
				// les dates ISO débordaient et cassaient l'alignement de la rangée.
				value={
					status.firstUsageDate
						? `${formatDwDay(status.firstUsageDate)} → ${formatDwDay(status.lastUsageDate)}`
						: 'aucune'
				}
				hint="Plage de journées relevées. Avant la première date, tous les écrans affichent zéro — ce qui ne veut pas dire que rien n’a été consommé."
			/>
			<Tile label="Lignes mesurées" value={status.rows.toLocaleString('fr-FR')} />
			{/* ⚠️ PAS d'alerte sur ce chiffre, et c'est délibéré : depuis que la
			    mesure couvre tout le compte de facturation, l'infrastructure de
			    Quanti (VM, stockage, secrets, réseau, registre) est non rattachée
			    PAR NATURE — 88 projets sur 103 en staging. Le colorer en orange le
			    ferait crier en permanence, et plus personne ne le regarderait le
			    jour où il signalerait vraiment un projet mort. */}
			<Tile
				label="Projets GCP sans projet Quanti"
				value={`${status.unattachedProjects} / ${status.gcpProjects}`}
				hint="Infrastructure Quanti, projets internes, et projets supprimés dont le projet GCP tourne encore. L’infrastructure en fait partie par nature : ce chiffre n’est pas une alerte."
			/>
		</Grid>
	)
}

// #endregion
